const encoder = new TextEncoder()

export interface StepUpTokenClaims {
  sub: string
  email: string
  sid: string
  action: string
  method?: 'email' | 'totp'
  iat: number
  exp: number
}

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000)
}

function encodeBase64Url(bytesOrText: Uint8Array | string): string {
  const bytes = typeof bytesOrText === 'string' ? encoder.encode(bytesOrText) : bytesOrText
  let base64: string
  if (typeof btoa === 'function') {
    let binary = ''
    for (const byte of bytes) binary += String.fromCharCode(byte)
    base64 = btoa(binary)
  } else {
    base64 = Buffer.from(bytes).toString('base64')
  }
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function decodeBase64UrlText(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=')
  if (typeof atob === 'function') {
    return atob(padded)
  }
  return Buffer.from(padded, 'base64').toString('utf8')
}

async function importHmacKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify'],
  )
}

async function signSignature(content: string, secret: string): Promise<string> {
  const key = await importHmacKey(secret)
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(content))
  return encodeBase64Url(new Uint8Array(signature))
}

export async function signStepUpToken(
  payload: Omit<StepUpTokenClaims, 'iat' | 'exp'>,
  secret: string,
  expiresInSeconds: number,
): Promise<string> {
  const header = { alg: 'HS256', typ: 'JWT' }
  const iat = nowSeconds()
  const claims: StepUpTokenClaims = {
    ...payload,
    iat,
    exp: iat + expiresInSeconds,
  }

  const encodedHeader = encodeBase64Url(JSON.stringify(header))
  const encodedPayload = encodeBase64Url(JSON.stringify(claims))
  const content = `${encodedHeader}.${encodedPayload}`
  const signature = await signSignature(content, secret)
  return `${content}.${signature}`
}

export async function verifyStepUpToken(token: string, secret: string): Promise<StepUpTokenClaims | null> {
  const [headerPart, payloadPart, signaturePart] = token.split('.')
  if (!headerPart || !payloadPart || !signaturePart) return null

  let header: { alg?: string; typ?: string } | null = null
  try {
    header = JSON.parse(decodeBase64UrlText(headerPart))
  } catch {
    return null
  }

  if (!header || header.alg !== 'HS256' || header.typ !== 'JWT') return null

  const signingContent = `${headerPart}.${payloadPart}`
  const expected = await signSignature(signingContent, secret)
  if (expected !== signaturePart) return null

  let claims: StepUpTokenClaims | null = null
  try {
    claims = JSON.parse(decodeBase64UrlText(payloadPart)) as StepUpTokenClaims
  } catch {
    return null
  }

  if (!claims?.sub || !claims.email || !claims.sid || !claims.action) return null
  if (typeof claims.iat !== 'number' || typeof claims.exp !== 'number') return null
  if (claims.exp <= nowSeconds()) return null

  return claims
}
